import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchUser, blockUsers, deleteUsers, unblockUsers } from '../../../services/users';
import { isAllowAction } from '../../../services/permissions/utils';
import { UserProfile } from '../../../types/api/users';
import { ReviewsCounts } from '../../../hooks/useUserReviewsQuery';
import { getUserReviewsCounts } from '../../../hooks/useUserReviewsQuery';
import { useAlert } from '../../../context/alert';
import { useUserContext } from '../../../context/user';
import TableActionsBanner from '../../common/TableActionsBanner';
import RegularPageLayout from '../../../layouts/RegularPageLayout';
import UserViewSegment from './components/UserViewSegment';
import UserCard from './components/UserCard';
import { StatsCard } from '../../basic/StatsCard';
import UserContacts from './components/UserContacts';
import UserLocations from './components/UserLocations';
import { UserReviewModal } from './components/UserReviewModal/UserReviewModal';
import { Stack, Button } from '@primer/react';
import { BlockedIcon, CodeOfConductIcon, TrashIcon } from '@primer/octicons-react';

export default function UserView() {
    type UserAction = 'block' | 'unblock' | 'delete';

    const { profile } = useUserContext();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [user, setUser] = useState<UserProfile | undefined>(undefined);
    const [reviewsCounter, setReviewsCounter] = useState<ReviewsCounts | undefined>(undefined);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const [openReviewModal, setOpenReviewModal] = React.useState(false)

    const { setInfo, removeAlert, updateAlert, addCustomAlert } = useAlert();

    const fetchedIdRef = useRef<string | null>(null);

    const { mutate: fetchUserById, isPending: isLoadingProfile } = useMutation({
        mutationFn: fetchUser,
        onSuccess: (data) => {
            setUser(data)
        },
        onError: (error: any) => {

        }
    });

    useEffect(() => {
        const id = searchParams.get('id');
        if (id && fetchedIdRef.current !== id && !isLoadingProfile) {
            fetchedIdRef.current = id;
            fetchUserById(id);

            getUserReviewsCounts(id)
                .then((counter) => {
                    setReviewsCounter(counter)
                })
                .catch((err) => {
                    console.error("Failed fetch reviews counter", err);
                });
        }
    }, [searchParams, fetchUserById, isLoadingProfile]);

    const fetchUserData = () => {
        const id = searchParams.get('id');
        if (id) {
            fetchUserById(id);
        }
    }

    // USERS ACTIONS
    const useUserAction = (action: UserAction, actionsAlertId?: string) => {
        return useCallback(() => {
            const id = searchParams.get('id');

            if (id) {
                switch (action) {
                    case 'block':
                        blockUsersById({ id });
                        break;
                    case 'unblock':
                        unblockUsersById({ id });
                        break;
                    case 'delete':
                        deleteUsersById({ id });
                        break;
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const blockSelectedUsers = useUserAction('block', actionsAlertId);
    const unblockSelectedUsers = useUserAction('unblock', actionsAlertId);
    const deleteSelectedUsers = useUserAction('delete', actionsAlertId);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        } else {
            let propsForAlert: any = undefined
            propsForAlert = customProps

            const alertId = addCustomAlert(
                TableActionsBanner,
                propsForAlert,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId]);

    // BLOCK USERS
    const { mutate: blockUsersById } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => blockUsers({ userIds: [id] }),
        onSuccess: (data) => {
            const username = user?.username || ''
            dismissTableActionAlert()
            setInfo(`Пользователь ${username} заблокирован`);
            fetchUserData();
        }
    });

    function createConfirmBlockAlertProps() {
        return {
            count: undefined,
            title: 'Точно хотите заблокировать пользователя?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissTableActionAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={blockSelectedUsers}>
                    Да, заблокировать
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const showConfirmBlockAlert = useCallback(() => {
        const props = createConfirmBlockAlertProps()
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // UNBLOCK USERS
    const { mutate: unblockUsersById } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => unblockUsers({ userIds: [id] }),
        onSuccess: (data) => {
            const username = user?.username || ''
            dismissTableActionAlert()
            setInfo(`Пользователь ${username} разблокирован`);
            fetchUserData();
        }
    });

    function createConfirmUnblockAlertProps() {
        return {
            count: undefined,
            title: 'Точно хотите разблокировать пользователя?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissTableActionAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="default" onClick={unblockSelectedUsers}>
                    Да
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const showConfirmUnblockAlert = useCallback(() => {
        const props = createConfirmUnblockAlertProps()
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // DELETE USERS
    const { mutate: deleteUsersById } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => deleteUsers({ userIds: [id] }),
        onSuccess: (data) => {
            const username = user?.username || ''
            dismissTableActionAlert()
            setInfo(`Пользователь ${username} удален`);
            navigate('/users');
        }
    });

    function createConfirmDeleteAlertProps() {
        return {
            count: undefined,
            title: 'Точно хотите удалить пользователя?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={dismissTableActionAlert}>
                    Нет
                </Button>,
                <Button key="clear-button" variant="danger" onClick={deleteSelectedUsers}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    const showConfirmDeleteAlert = useCallback(() => {
        const props = createConfirmDeleteAlertProps()
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const openReviewsPage = () => setOpenReviewModal(true)


    return (
        <RegularPageLayout
            header={
                <UserViewSegment id={searchParams.get('id') || ''} />
            }
            content={
                <Stack>
                    {user && <UserReviewModal
                        isOpen={openReviewModal}
                        userId={user.id}
                        counter={reviewsCounter ?? { fromBuyers: 0, fromSellers: 0 }}
                        onClose={() => setOpenReviewModal(false)}
                    />}
                    {user &&
                        <Stack className='mt-7 mx-auto'>
                            <Stack justify="space-between" direction="horizontal" className='mb-7'>
                                <UserCard
                                    user={user}
                                    isShowStatus={true}
                                    rating={user.reviewsStats?.rating || 0}
                                    nameFontSize={20}
                                    ratingFontSize={16}
                                    ratingHeight={16}
                                />

                                <Stack direction="horizontal" align="center">
                                    {user.isBlocked ? (
                                        isAllowAction(profile, 'BLOCK_USERS') && (
                                            <Button
                                                key="unblock-button"
                                                variant="default"
                                                leadingVisual={CodeOfConductIcon}
                                                onClick={showConfirmUnblockAlert}
                                            >
                                                Разблокировать
                                            </Button>
                                        )
                                    ) : (
                                        isAllowAction(profile, 'BLOCK_USERS') && (
                                            <Button
                                                key="block-button"
                                                variant="default"
                                                leadingVisual={BlockedIcon}
                                                onClick={showConfirmBlockAlert}
                                            >
                                                Заблокировать
                                            </Button>
                                        )
                                    )}
                                    {isAllowAction(profile, 'BLOCK_USERS') && (<Button key="clear-button" variant="default" leadingVisual={TrashIcon} onClick={showConfirmDeleteAlert}>
                                        Удалить
                                    </Button>)}
                                </Stack>
                            </Stack>

                            <Stack
                                className='mb-7'
                                direction="horizontal"
                                align="center">
                                <StatsCard
                                    title="Отзывы"
                                    count={(reviewsCounter?.fromBuyers ?? 0) + (reviewsCounter?.fromSellers ?? 0)}
                                    isAllowClick={true}
                                    onClick={openReviewsPage} />
                                <StatsCard
                                    title="Сделки"
                                    count={user.dealsCount}
                                    isAllowClick={false} />
                                <StatsCard
                                    title="Подписчики"
                                    count={user.subscribersCount}
                                    isAllowClick={false} />
                            </Stack>

                            <UserContacts user={user} className='border-t border-b border-separator pt-7 pb-7' />
                            <UserLocations
                                className='mt-7'
                                registrationAddress={user.registrationAddress}
                                userAddress={user.address} />
                        </Stack>
                    }
                </Stack>
            }
        />
    )
}